import { Box, HStack, Portal, Stack, Text } from '@chakra-ui/react';
import { useEffect } from 'react';

import { i18n } from '#features/i18n/i18n';
import { envConfig } from '#constants/envConfig';
import { PLAY_STORE_URL } from '#constants/externalLinks';
import { MAINTENANCE_RECHECK_MS } from '../../constants';
import { MAINTENANCE_COPY, UPDATE_COPY } from '../../constants/copy';
import { PageHeading, asButton, Button } from '#features/ui';
import { useAppUpdateGate } from '../../hooks/useAppUpdateGate';
import { useFlexibleUpdate } from '../../hooks/useFlexibleUpdate';

/**
 * Время возвращения — человеку, а не машине: «01:30», в его часовом поясе.
 *
 * Сервер присылает строку ISO, и она может оказаться какой угодно — поле правят руками в
 * админке. Не разобралось — не показываем ничего: строка вида «2026-09-02T01:30:00Z» на
 * экране заглушки хуже её отсутствия.
 *
 * Прошедший срок не показываем вовсе. Работы затягиваются чаще, чем заканчиваются раньше, и в
 * три часа ночи обещание «вернёмся в 01:30» — единственная проверяемая цифра на экране, и она
 * врёт. Молчание в этом месте честнее.
 *
 * Дату дописываем, когда срок не сегодняшний: «вернёмся в 09:00» на ночном выкате читается как
 * «вот-вот», хотя ждать девять часов.
 */
const formatUntil = (iso: string, now: number): string | null => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime()) || at.getTime() <= now) {
    return null;
  }
  const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (new Date(now).toDateString() === at.toDateString()) {
    return time;
  }
  return `${at.toLocaleDateString([], { day: '2-digit', month: '2-digit' })} ${time}`;
};

/**
 * Увести человека обновляться. Три попытки по убыванию удобства, каждая следующая — фолбэк
 * предыдущей, потому что верхние две доступны далеко не всегда (только установка из Play, тот
 * же ключ подписи, аккаунт-владелец; на эмуляторе и при sideload — ничего из этого).
 *
 *  1. `immediate` — полноэкранный поток Play прямо в приложении: ставит и перезапускает сам.
 *     Просим только на экране блокировки: там человеку всё равно некуда идти.
 *  2. `market://` через нативный плагин — открывает приложение магазина.
 *  3. обычная ссылка в системном браузере (`@capacitor/browser`, он уже стоит ради OAuth).
 */
const goToStore = (url: string | null, immediate: boolean) => {
  void (async () => {
    // Гейт нужен не только для порядка: без него Rollup всё равно выделяет чанк плагина в
    // веб-сборке, где его некому загрузить. `IS_NATIVE` — константа, ветка сворачивается.
    if (envConfig.IS_NATIVE) {
      const play = await import('../../utils/playUpdate');

      // Согласие на immediate означает, что процесс сейчас убьют, — сюда мы уже не вернёмся.
      if (immediate && (await play.startImmediateUpdate())) {
        return;
      }

      if (await play.openStore()) {
        return;
      }
    }

    if (!url) {
      return;
    }

    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
    } catch {
      // Плагина нет — уходим обычной навигацией, хуже уже не будет.
      window.location.href = url;
    }
  })();
};

/**
 * Баннер «доступна новая версия» и экран жёсткой блокировки.
 *
 * Только для приложения: в вебе обновление доезжает само перезагрузкой страницы, и звать
 * веб-пользователя в магазин бессмысленно. Гейт стоит первым оператором и опирается на
 * `IS_NATIVE` в форме `=== 'true'` — константа сворачивается, и Capacitor не утекает в
 * веб-бандл вместе с этим компонентом.
 */
export const AppUpdateGate = () => {
  const { decision, maintenance, platform, dismiss, recheck } = useAppUpdateGate();
  /*
   * Play спрашиваем только пока баннер на экране. На экране блокировки flexible неуместен:
   * там человеку всё равно нечем пользоваться, и обновление должно быть immediate.
   */
  const flexible = useFlexibleUpdate(decision.kind === 'banner');

  /**
   * Пока идут работы, экран перепроверяет состояние сам. Обычный интервал проверки — полчаса,
   * и без этого таймера человек сидел бы перед заглушкой ещё полчаса после того, как всё
   * починили. Таймер живёт только вместе с экраном: в обычном состоянии приложение не должно
   * дёргать конфиг каждые полминуты.
   */
  useEffect(() => {
    if (!envConfig.IS_NATIVE || !maintenance.enabled) {
      return;
    }
    const id = window.setInterval(recheck, MAINTENANCE_RECHECK_MS);
    return () => window.clearInterval(id);
  }, [maintenance.enabled, recheck]);

  if (!envConfig.IS_NATIVE) {
    return null;
  }

  /**
   * Работы важнее обновления: чинить нечего, пока сервер не вернулся, и звать человека в
   * магазин в этот момент — врать. Поэтому ветка стоит первой и перекрывает и баннер, и блок.
   */
  if (maintenance.enabled) {
    const m = MAINTENANCE_COPY[i18n.locale === 'ru' ? 'ru' : 'en'];
    // Пересчитывается на каждой перерисовке, а она случается вместе с очередной проверкой раз
    // в полминуты — значит просроченное обещание само уйдёт с экрана, никого не дожидаясь.
    const untilText = maintenance.until ? formatUntil(maintenance.until, Date.now()) : null;
    return (
      <Portal>
        <Box
          position="fixed"
          // Смещения по одному — та же причина, что и у экрана блокировки ниже: `inset` как
          // проп Chakra не раскрывает, и экран молча уезжает за пределы вида.
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex="modal"
          bg="bg"
          pt="env(safe-area-inset-top, 0px)"
          pb="env(safe-area-inset-bottom, 0px)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={6}
        >
          <Stack gap={5} maxW="380px" textAlign="center" alignItems="center">
            <PageHeading>
              {m.title}
            </PageHeading>
            {/* Причину пишет человек в админке на своём языке — показываем как есть.
                Её нет — остаётся общая фраза, которая уже всё главное сказала. */}
            <Text textStyle="body" color="fg.secondary">
              {maintenance.reason ?? m.desc}
            </Text>
            {untilText ? (
              <Text textStyle="caption" color="fg.muted">
                {`${m.until} ${untilText}`}
              </Text>
            ) : null}
            {/* Кнопка есть, хотя экран перепроверяет себя сам: ждать полминуты, глядя на
                заглушку, невыносимо, а нажатие возвращает управление человеку. */}
            <Text {...asButton} textStyle="caption" color="fg.muted" cursor="pointer" onClick={recheck}>
              {m.action}
            </Text>
          </Stack>
        </Box>
      </Portal>
    );
  }

  if (decision.kind === 'none') {
    return null;
  }

  const t = UPDATE_COPY[i18n.locale === 'ru' ? 'ru' : 'en'];
  // На iOS листинга ещё нет, поэтому фолбэка на константу там быть не может: ведём только
  // туда, что явно задано в конфиге.
  const storeUrl = decision.storeUrl ?? (platform === 'ios' ? null : PLAY_STORE_URL);

  if (decision.kind === 'block') {
    return (
      <Portal>
        <Box
          position="fixed"
          // Смещения по одному, а не `inset`: Chakra его как проп не раскрывает, и на устройстве
          // это молча превращалось в `position: fixed` без координат. Экран честно отрисовывался
          // и попадал в DOM, но вставал в конец документа за пределами вида — приложение
          // выглядело работающим при поднятом пороге. Поймано только прогоном на телефоне.
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex="modal"
          bg="bg"
          pt="env(safe-area-inset-top, 0px)"
          pb="env(safe-area-inset-bottom, 0px)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={6}
        >
          <Stack gap={5} maxW="380px" textAlign="center" alignItems="center">
            <PageHeading>
              {t.blockTitle}
            </PageHeading>
            <Text textStyle="body" color="fg.secondary">
              {t.blockDesc}
            </Text>
            {storeUrl ? (
              <Button
                w="100%"
                size="lg"
                bgColor="purple.solid"
                color="white"
                onClick={() => goToStore(storeUrl, true)}
              >
                {t.blockAction}
              </Button>
            ) : null}
            {/*
              Единственный выход из экрана, и он обязателен. Порог поднимается руками, а
              руками же его можно поднять по ошибке — тогда без этой кнопки приложение
              «залипнет» до следующего изменения конфига, которое человек никак не увидит.
              Здесь же он перечитывает /app/status.json и снимает блок, как только порог вернули.
              Кнопки «выйти» намеренно нет: уйти можно домашним экраном.
            */}
            <Text
              {...asButton}
              textStyle="caption"
              color="fg.muted"
              cursor="pointer"
              onClick={() => window.location.reload()}
            >
              {t.blockRetry}
            </Text>
          </Stack>
        </Box>
      </Portal>
    );
  }

  return (
    <Portal>
      <Box
        position="fixed"
        top="env(safe-area-inset-top, 0px)"
        left={0}
        right={0}
        zIndex="banner"
        bg="bg.surface"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        px={4}
        py={3}
      >
        <HStack gap={3} alignItems="center">
          <Stack gap={0.5} flex={1} minW={0}>
            <Text textStyle="title">{t.bannerTitle}</Text>
            <Text textStyle="caption" color="fg.muted">
              {t.bannerDesc}
            </Text>
          </Stack>
          {/*
            Три состояния одной кнопки. «Обновить» зовёт нативный лист Play (flexible): он качает
            в фоне, приложением можно пользоваться дальше. Уход в магазин остался фолбэком на
            случай, когда Play отказывает — sideload, эмулятор, чужой аккаунт, нет Play Services.
            Установка не начинается сама: скачанное ставится только по «Перезапустить».
          */}
          {flexible.stage === 'downloading' ? (
            <Text textStyle="caption" color="fg.muted" flexShrink={0}>
              {t.bannerDownloading}
            </Text>
          ) : flexible.stage === 'ready' ? (
            <Button size="sm" bgColor="purple.solid" color="white" onClick={flexible.complete}>
              {t.bannerRestart}
            </Button>
          ) : storeUrl ? (
            <Button
              size="sm"
              bgColor="purple.solid"
              color="white"
              onClick={() => flexible.start(() => goToStore(storeUrl, false))}
            >
              {t.bannerAction}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" color="fg.muted" onClick={dismiss}>
            {t.bannerDismiss}
          </Button>
        </HStack>
      </Box>
    </Portal>
  );
};
